import type {
  BookCreateInput,
  BookDetail,
  BookListItem,
  BookNote,
  BookNoteInput,
  BookReadingLogEntry,
  BookReadingLogInput,
  BookUpdateInput,
  DashboardResponse,
  FinanceAccount,
  FinanceAccountInput,
  FinanceCategory,
  FinanceFund,
  FinanceFundInput,
  FinanceSummary,
  FinanceTransaction,
  FinanceTransactionInput,
  FinanceTransactionSplitRequest,
  FinanceTransactionSplitResponse,
  FinanceTransactionType,
  FinanceTransactionUpdateInput,
  FinanceTransfer,
  FinanceTransferInput,
  ListResponse,
  MediaCreateInput,
  MediaDetail,
  MediaListItem,
  MediaProgressInput,
  MediaUpdateInput,
  PlaceDetail,
  PlaceListItem,
  PlaceNote,
  PlaceNoteInput,
  PlaceUpdateInput,
  PlaceVisit,
  PlaceVisitInput,
  SearchResponse,
  TmdbMediaType,
  TmdbSearchResponse,
} from "@atlas/contracts";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: "same-origin", signal });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // Response body wasn't JSON; keep the generic status message.
    }
    throw new ApiError(message, res.status);
  }

  return (await res.json()) as T;
}

async function postJson<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...extraHeaders },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const responseBody = (await res.json()) as { error?: string };
      if (responseBody?.error) message = responseBody.error;
    } catch {
      // Response body wasn't JSON; keep the generic status message.
    }
    throw new ApiError(message, res.status);
  }

  return (await res.json()) as T;
}

async function patchJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const responseBody = (await res.json()) as { error?: string };
      if (responseBody?.error) message = responseBody.error;
    } catch {
      // Response body wasn't JSON; keep the generic status message.
    }
    throw new ApiError(message, res.status);
  }

  return (await res.json()) as T;
}

async function deleteRequest(path: string, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`${API_BASE_URL}${path}`, { method: "DELETE", credentials: "same-origin", signal });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // Response body wasn't JSON; keep the generic status message.
    }
    throw new ApiError(message, res.status);
  }
}

interface AuthStatusResponse {
  authenticated: boolean;
}

/** Checks the current session. Never throws on 401 — that's the normal "not logged in" outcome. */
export async function checkAuth(signal?: AbortSignal): Promise<boolean> {
  const res = await fetch(`${API_BASE_URL}/api/auth/me`, { credentials: "same-origin", signal });
  if (res.status === 401) return false;
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status);
  const body = (await res.json()) as AuthStatusResponse;
  return body.authenticated;
}

export async function login(password: string, signal?: AbortSignal): Promise<void> {
  await postJson<AuthStatusResponse>("/api/auth/login", { password }, signal);
}

export async function logout(signal?: AbortSignal): Promise<void> {
  await postJson<AuthStatusResponse>("/api/auth/logout", {}, signal);
}

export function fetchDashboard(signal?: AbortSignal): Promise<DashboardResponse> {
  return getJson<DashboardResponse>("/api/dashboard", signal);
}

export function fetchSearch(query: string, signal?: AbortSignal): Promise<SearchResponse> {
  return getJson<SearchResponse>(`/api/search?q=${encodeURIComponent(query)}`, signal);
}

function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, string | number | undefined>)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export interface BooksQuery {
  status?: "unread" | "reading" | "read";
  q?: string;
  limit?: number;
  offset?: number;
}

export function fetchBooks(params: BooksQuery = {}, signal?: AbortSignal): Promise<ListResponse<BookListItem>> {
  return getJson<ListResponse<BookListItem>>(`/api/books${buildQuery(params)}`, signal);
}

export function fetchBook(slug: string, signal?: AbortSignal): Promise<BookDetail> {
  return getJson<BookDetail>(`/api/books/${encodeURIComponent(slug)}`, signal);
}

/**
 * `idempotencyKey` should be a fresh value per intended submit, reused only
 * when retrying that exact same submit (e.g. after a network failure) so a
 * retry can never create a second book.
 */
export function createBook(input: BookCreateInput, idempotencyKey: string, signal?: AbortSignal): Promise<BookDetail> {
  return postJson<BookDetail>("/api/books", input, signal, { "Idempotency-Key": idempotencyKey });
}

export interface MediaQuery {
  status?: "want" | "watch" | "finished" | "dropped";
  type?: "movie" | "series";
  q?: string;
  limit?: number;
  offset?: number;
}

export function fetchMedia(params: MediaQuery = {}, signal?: AbortSignal): Promise<ListResponse<MediaListItem>> {
  return getJson<ListResponse<MediaListItem>>(`/api/media${buildQuery(params)}`, signal);
}

export function fetchMediaItem(slug: string, signal?: AbortSignal): Promise<MediaDetail> {
  return getJson<MediaDetail>(`/api/media/${encodeURIComponent(slug)}`, signal);
}

export interface PlacesQuery {
  status?: "want" | "visited" | "again";
  category?: "food" | "cafe" | "entertainment" | "travel";
  q?: string;
  limit?: number;
  offset?: number;
}

export function fetchPlaces(params: PlacesQuery = {}, signal?: AbortSignal): Promise<ListResponse<PlaceListItem>> {
  return getJson<ListResponse<PlaceListItem>>(`/api/places${buildQuery(params)}`, signal);
}

export function fetchPlace(slug: string, signal?: AbortSignal): Promise<PlaceDetail> {
  return getJson<PlaceDetail>(`/api/places/${encodeURIComponent(slug)}`, signal);
}

// Minimal write API (edit, status change, notes/logs, delete) — see
// packages/contracts for the request shapes and apps/api/src/routes for the
// endpoints these call.

export function updateBook(slug: string, input: BookUpdateInput, signal?: AbortSignal): Promise<BookDetail> {
  return patchJson<BookDetail>(`/api/books/${encodeURIComponent(slug)}`, input, signal);
}

export function addBookNote(slug: string, input: BookNoteInput, signal?: AbortSignal): Promise<BookNote> {
  return postJson<BookNote>(`/api/books/${encodeURIComponent(slug)}/notes`, input, signal);
}

export function addReadingLogEntry(
  slug: string,
  input: BookReadingLogInput,
  signal?: AbortSignal,
): Promise<BookReadingLogEntry> {
  return postJson<BookReadingLogEntry>(`/api/books/${encodeURIComponent(slug)}/reading-log`, input, signal);
}

export function deleteBook(slug: string, signal?: AbortSignal): Promise<void> {
  return deleteRequest(`/api/books/${encodeURIComponent(slug)}`, signal);
}

export function updateMedia(slug: string, input: MediaUpdateInput, signal?: AbortSignal): Promise<MediaDetail> {
  return patchJson<MediaDetail>(`/api/media/${encodeURIComponent(slug)}`, input, signal);
}

export function addMediaProgress(
  slug: string,
  input: MediaProgressInput,
  signal?: AbortSignal,
): Promise<MediaDetail> {
  return postJson<MediaDetail>(`/api/media/${encodeURIComponent(slug)}/progress`, input, signal);
}

export function deleteMedia(slug: string, signal?: AbortSignal): Promise<void> {
  return deleteRequest(`/api/media/${encodeURIComponent(slug)}`, signal);
}

export function searchTmdbMedia(
  type: TmdbMediaType,
  q: string,
  signal?: AbortSignal,
): Promise<TmdbSearchResponse> {
  return getJson<TmdbSearchResponse>(`/api/media/providers/tmdb/search${buildQuery({ type, q })}`, signal);
}

/**
 * `idempotencyKey` should be a fresh value per intended submit, reused only
 * when retrying that exact same submit, so a retry can never create a
 * second media item for the same TMDB title.
 */
export function createMediaItem(
  input: MediaCreateInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<MediaDetail> {
  return postJson<MediaDetail>("/api/media", input, signal, { "Idempotency-Key": idempotencyKey });
}

export function updatePlace(slug: string, input: PlaceUpdateInput, signal?: AbortSignal): Promise<PlaceDetail> {
  return patchJson<PlaceDetail>(`/api/places/${encodeURIComponent(slug)}`, input, signal);
}

export function addPlaceNote(slug: string, input: PlaceNoteInput, signal?: AbortSignal): Promise<PlaceNote> {
  return postJson<PlaceNote>(`/api/places/${encodeURIComponent(slug)}/notes`, input, signal);
}

export function addPlaceVisit(slug: string, input: PlaceVisitInput, signal?: AbortSignal): Promise<PlaceVisit> {
  return postJson<PlaceVisit>(`/api/places/${encodeURIComponent(slug)}/visits`, input, signal);
}

export function deletePlace(slug: string, signal?: AbortSignal): Promise<void> {
  return deleteRequest(`/api/places/${encodeURIComponent(slug)}`, signal);
}

// Finance domain — accounts, budget funds, and income/expense transactions.

export function fetchFinanceSummary(month?: string, signal?: AbortSignal): Promise<FinanceSummary> {
  return getJson<FinanceSummary>(`/api/finance/summary${buildQuery({ month })}`, signal);
}

export function fetchFinanceAccounts(signal?: AbortSignal): Promise<FinanceAccount[]> {
  return getJson<FinanceAccount[]>("/api/finance/accounts", signal);
}

export function createFinanceAccount(input: FinanceAccountInput, signal?: AbortSignal): Promise<FinanceAccount> {
  return postJson<FinanceAccount>("/api/finance/accounts", input, signal);
}

export function fetchFinanceFunds(month?: string, signal?: AbortSignal): Promise<FinanceFund[]> {
  return getJson<FinanceFund[]>(`/api/finance/funds${buildQuery({ month })}`, signal);
}

export function createFinanceFund(input: FinanceFundInput, signal?: AbortSignal): Promise<FinanceFund> {
  return postJson<FinanceFund>("/api/finance/funds", input, signal);
}

export interface FinanceTransactionsQuery {
  month?: string;
  type?: FinanceTransactionType;
  accountId?: string;
  fundId?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export function fetchFinanceTransactions(
  params: FinanceTransactionsQuery = {},
  signal?: AbortSignal,
): Promise<ListResponse<FinanceTransaction>> {
  return getJson<ListResponse<FinanceTransaction>>(`/api/finance/transactions${buildQuery(params)}`, signal);
}

export function createFinanceTransaction(
  input: FinanceTransactionInput,
  signal?: AbortSignal,
): Promise<FinanceTransaction> {
  return postJson<FinanceTransaction>("/api/finance/transactions", input, signal);
}

export function updateFinanceTransaction(
  id: string,
  input: FinanceTransactionUpdateInput,
  signal?: AbortSignal,
): Promise<FinanceTransaction> {
  return patchJson<FinanceTransaction>(`/api/finance/transactions/${encodeURIComponent(id)}`, input, signal);
}

export function fetchFinanceCategories(
  kind?: FinanceTransactionType,
  signal?: AbortSignal,
): Promise<FinanceCategory[]> {
  return getJson<FinanceCategory[]>(`/api/finance/categories${buildQuery({ kind })}`, signal);
}

export function splitFinanceTransaction(
  id: string,
  input: FinanceTransactionSplitRequest,
  signal?: AbortSignal,
): Promise<FinanceTransactionSplitResponse> {
  return postJson<FinanceTransactionSplitResponse>(`/api/finance/transactions/${encodeURIComponent(id)}/split`, input, signal);
}

export interface FinanceTransfersQuery {
  month?: string;
  accountId?: string;
  limit?: number;
  offset?: number;
}

export function fetchFinanceTransfers(
  params: FinanceTransfersQuery = {},
  signal?: AbortSignal,
): Promise<ListResponse<FinanceTransfer>> {
  return getJson<ListResponse<FinanceTransfer>>(`/api/finance/transfers${buildQuery(params)}`, signal);
}

export function createFinanceTransfer(input: FinanceTransferInput, signal?: AbortSignal): Promise<FinanceTransfer> {
  return postJson<FinanceTransfer>("/api/finance/transfers", input, signal);
}
